import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube, Clock, ShieldCheck, GitCommit, Rocket } from "lucide-react";
import { useSettings, isOpenNow } from "../../lib/useSettings";
import { api } from "../../lib/api";

export default function Footer() {
  const { settings: s } = useSettings();
  const navigate = useNavigate();
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);

  const fetchVersion = async () => {
    if (versionInfo) return;
    try { setVersionInfo((await api.get("/version")).data); }
    catch { setVersionInfo({ version: s?.version || "1.0.0", deployed_at: null, build_time: null }); }
  };

  const toggleVersion = () => {
    if (!versionOpen) fetchVersion();
    setVersionOpen((v) => !v);
  };

  useEffect(() => {
    if (!versionOpen) return;
    const onEsc = (e) => e.key === "Escape" && setVersionOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [versionOpen]);

  if (!s) return null;
  const status = isOpenNow(s.hours || []);
  const socialIcons = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube };

  return (
    <footer className="bg-[color:var(--brand-navy)] text-white noise-bg" data-testid="public-footer">
      {/* Top row: Presentation block (full width) */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-14 md:pt-16 pb-10 md:pb-12 border-b border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center">
          {s.logo_url && (
            <img src={s.logo_url} alt={s.company_name} className="h-20 md:h-24 w-auto" />
          )}
          <div>
            <h3 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">{s.company_name}</h3>
            {s.footer_description ? (
              <p className="text-white/80 text-sm md:text-[15px] leading-relaxed whitespace-pre-line max-w-3xl">{s.footer_description}</p>
            ) : (
              <p className="text-white/70 text-sm md:text-base leading-relaxed">{s.tagline}</p>
            )}
          </div>
        </div>
      </div>

      {/* Middle row: 4 evenly-weighted information columns */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-10 items-start">
        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 text-[color:var(--brand-sky)]">Navigation</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="text-white/80 hover:text-white">Services</Link></li>
            <li><Link to="/realisations" className="text-white/80 hover:text-white">Réalisations</Link></li>
            <li><Link to="/blog" className="text-white/80 hover:text-white">Blog</Link></li>
            <li><Link to="/contact" className="text-white/80 hover:text-white">Contact</Link></li>
            <li><Link to="/devis" className="text-white/80 hover:text-white">Devis gratuit</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 text-[color:var(--brand-sky)]">Contact</h4>
          <ul className="space-y-3 text-sm">
            {s.phone && (
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0"/><a href={`tel:${s.phone.replace(/\s/g,"")}`} className="text-white/90 hover:text-white">{s.phone}</a></li>
            )}
            {s.email && (
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0"/><a href={`mailto:${s.email}`} className="text-white/90 hover:text-white break-all">{s.email}</a></li>
            )}
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0"/>
              <span className="text-white/90">{s.address_public}<br/><span className="text-white/60 text-xs">{s.address_appointment}</span></span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 text-[color:var(--brand-sky)]">Horaires</h4>
          <ul className="space-y-1 text-xs text-white/80 font-body">
            {(s.hours || []).map((h, i) => {
              const isToday = i === ((new Date().getDay() + 6) % 7);
              return (
                <li key={i} className={`relative flex justify-between gap-3 px-2 py-1 rounded-md transition-colors ${isToday ? "bg-[color:var(--brand-sky)]/15 text-white" : ""}`}>
                  {isToday && <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-[color:var(--brand-sky)]" aria-hidden />}
                  <span className={isToday ? "font-semibold" : ""}>
                    {h.day}
                    {isToday && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[color:var(--brand-sky)] font-bold">Auj.</span>}
                  </span>
                  <span className="tabular-nums">{h.closed ? "Fermé" : `${h.morning || ""} ${h.afternoon ? "· "+h.afternoon : ""}`.trim()}</span>
                </li>
              );
            })}
          </ul>
          {s.vacation?.message && (
            <p className="mt-3 text-xs bg-[color:var(--brand-red)]/20 text-white px-3 py-2 rounded">
              {s.vacation.message}
            </p>
          )}
        </div>

        <div>
          <h4 className="font-display font-semibold text-sm uppercase tracking-wider mb-4 text-[color:var(--brand-sky)]">Suivez-nous</h4>
          <div className="flex items-start gap-2 text-sm text-white/90 mb-4">
            <Clock className="w-4 h-4 mt-0.5 text-[color:var(--brand-sky)] flex-shrink-0" />
            <span>{status.label}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.social || {}).filter(([_, v]) => v).map(([k, v]) => {
              const Icon = socialIcons[k] || Facebook;
              return (
                <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[color:var(--brand-sky)] flex items-center justify-center transition-colors" aria-label={k} data-testid={`social-${k}`}>
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
          <Link
            to="/devis"
            className="mt-5 inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-xs shadow-lg transition-all hover:-translate-y-0.5"
            data-testid="footer-cta-devis"
          >
            Devis gratuit 24h
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 pb-24 md:pb-8 flex flex-col items-center justify-center gap-4 text-xs text-white/60 text-center">
          <div className="flex flex-col items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Site développé par</span>
            <a
              href="https://agence-msc.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center transition-transform hover:-translate-y-0.5"
              aria-label="Agence MSC — site web officiel"
              data-testid="footer-msc-logo"
            >
              <img
                src="/uploads/logo-msc.webp"
                alt="Agence MSC"
                className="h-7 md:h-8 w-auto"
                loading="lazy"
              />
            </a>
          </div>

          {/* Accès back-office — icône seule, hors zone du FAB */}
          <button
            type="button"
            onClick={() => navigate("/admin/login")}
            title="Accès sécurisé back-office"
            aria-label="Accès back-office"
            className="relative z-50 self-center inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/5 hover:bg-[color:var(--brand-sky)]/20 active:bg-[color:var(--brand-sky)]/30 text-white/50 hover:text-[color:var(--brand-sky)] border border-white/10 hover:border-[color:var(--brand-sky)]/40 transition-colors cursor-pointer"
            data-testid="footer-admin-access"
          >
            <ShieldCheck className="w-4 h-4" />
          </button>

          <div className="flex gap-4 flex-wrap justify-center items-center text-white/60">
            <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
            <Link to="/cgv" className="hover:text-white">CGV</Link>
            <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
            <span className="text-white/30">·</span>
            <div className="relative">
              <button
                type="button"
                onClick={toggleVersion}
                className="hover:text-white cursor-pointer transition inline-flex items-center gap-1 font-mono"
                aria-label="Informations de version"
                data-testid="footer-version-btn"
              >
                v{s.version || "1.0.0"}
              </button>
              {versionOpen && (
                <>
                  <button type="button" onClick={() => setVersionOpen(false)} className="fixed inset-0 z-40 cursor-default" aria-hidden />
                  <div className="absolute bottom-full right-0 md:right-auto md:left-1/2 md:-translate-x-1/2 mb-2 w-64 bg-white text-[color:var(--brand-navy)] rounded-xl shadow-2xl border border-[hsl(var(--border))] p-4 z-50 animate-fade-up" data-testid="footer-version-popover">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-bold mb-2">Version du site</div>
                    <div className="flex items-center gap-2 mb-3">
                      <GitCommit className="w-4 h-4 text-[color:var(--brand-sky)]" />
                      <span className="font-display font-bold text-lg">v{(versionInfo?.version) || s.version || "1.0.0"}</span>
                    </div>
                    <div className="space-y-2 text-xs">
                      {versionInfo?.deployed_at && (
                        <div className="flex items-start gap-2">
                          <Rocket className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Déployé le</div>
                            <div className="font-mono font-semibold">
                              {new Date(versionInfo.deployed_at).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                        </div>
                      )}
                      {versionInfo?.build_time && versionInfo.build_time !== versionInfo.deployed_at && (
                        <div className="flex items-start gap-2">
                          <GitCommit className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Dernière mise à jour</div>
                            <div className="font-mono font-semibold">
                              {new Date(versionInfo.build_time).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                        </div>
                      )}
                      {!versionInfo && <div className="text-[hsl(var(--muted-foreground))] font-mono">Chargement…</div>}
                    </div>
                    <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] text-[10px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                      Site propulsé par l'agence <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="font-semibold text-[color:var(--brand-sky)] hover:underline">MSC</a>.
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="text-white/50">
            © {new Date().getFullYear()} {s.company_name} — Tous droits réservés
          </div>
        </div>
      </div>
    </footer>
  );
}
